(() => {
  const siteId = document.currentScript?.dataset.hotjarSiteId;
  const numericSiteId = Number(siteId);

  if (
    !/^[1-9]\d*$/.test(siteId || '') ||
    !Number.isSafeInteger(numericSiteId) ||
    window.Shopify?.designMode ||
    window.zeroCableHotjarConsent
  ) {
    return;
  }
  window.zeroCableHotjarConsent = true;

  let trackerRequested = false;
  let reloading = false;
  let loadingPrivacy = false;
  let retryCount = 0;
  const consentNotificationKey = 'zerocable:hotjar-consent-change';

  function hasAnalyticsConsent() {
    const privacy = window.Shopify?.customerPrivacy;

    try {
      // Shopify can allow analytics without opt-in in some regions. Require both
      // explicit consent and Shopify's current processing permission everywhere.
      return privacy?.currentVisitorConsent().analytics === 'yes' && privacy.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  }

  function clearHotjarData() {
    try {
      const domains = window.location.hostname.split('.');
      const cookieNames = document.cookie.split(';').map((cookie) => cookie.trim().split('=')[0]);

      for (const name of cookieNames) {
        if (!name?.startsWith('_hj')) continue;

        const expiredCookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = expiredCookie;

        // Hotjar uses the highest available parent domain, including on www.
        for (let index = 0; index < domains.length - 1; index++) {
          document.cookie = `${expiredCookie}; domain=${domains.slice(index).join('.')}`;
        }
      }
    } catch {
      // Cookie restrictions must never prevent consent withdrawal and reload.
    }

    // Access storage inside the try block: the property getters can also throw.
    for (const getStorage of [() => window.localStorage, () => window.sessionStorage]) {
      try {
        const storage = getStorage();
        for (let index = storage.length - 1; index >= 0; index--) {
          const key = storage.key(index);
          if (key?.startsWith('_hj') || key === 'hjActiveViewportIds' || key === 'hjViewportId') {
            storage.removeItem(key);
          }
        }
      } catch {
        // Browsers can disable access to web storage.
      }
    }
  }

  function loadHotjar() {
    if (trackerRequested || reloading) return;
    trackerRequested = true;

    if (!window.hj) {
      /** @type {HotjarCommand} */
      const queue = (...args) => {
        (queue.q = queue.q || []).push(args);
      };
      window.hj = queue;
    }
    window._hjSettings = { hjid: numericSiteId, hjsv: 6 };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${siteId}.js?sv=6`;
    document.head.appendChild(script);
  }

  /** @param {boolean} [analyticsAllowed] - The consent change's processing permission. */
  function updateConsent(analyticsAllowed = true) {
    if (reloading) return;

    // Also honor an explicit denial in the event while Shopify updates its API.
    if (analyticsAllowed && hasAnalyticsConsent()) {
      loadHotjar();
      return;
    }

    // Repeat on the next denied page to clear anything written during unload.
    clearHotjarData();

    if (trackerRequested) {
      reloading = true;
      // Hotjar has no supported stop API. A full navigation is needed to tear
      // down its recorder, including when its script is still downloading.
      window.location.reload();
    }
  }

  function initializePrivacy() {
    if (window.Shopify?.customerPrivacy) {
      updateConsent();
      return;
    }

    if (loadingPrivacy) return;

    if (typeof window.Shopify?.loadFeatures === 'function') {
      loadingPrivacy = true;
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          loadingPrivacy = false;
          if (!error) updateConsent();
        });
      } catch {
        loadingPrivacy = false;
        // Fail closed: never load Hotjar if Shopify's privacy API fails.
      }
      return;
    }

    // content_for_header normally provides loadFeatures before this deferred
    // script runs. Allow a delayed Shopify bootstrap without polling forever.
    if (retryCount++ < 40) window.setTimeout(initializePrivacy, 250);
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    const analyticsAllowed = event.detail?.analyticsAllowed !== false;
    try {
      // Notify other open storefront tabs. Shopify remains the source of truth;
      // this value is only a notification, never a substitute for its API.
      window.localStorage.setItem(
        consentNotificationKey,
        JSON.stringify({
          analyticsAllowed: analyticsAllowed && hasAnalyticsConsent(),
          changedAt: Date.now(),
        })
      );
    } catch {
      // Consent still works in this tab if browser storage is unavailable.
    }
    updateConsent(analyticsAllowed);
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== consentNotificationKey || !event.newValue) return;
    try {
      /** @type {unknown} */
      const notification = JSON.parse(event.newValue);
      if (
        typeof notification === 'object' &&
        notification !== null &&
        'analyticsAllowed' in notification &&
        typeof notification.analyticsAllowed === 'boolean'
      ) {
        updateConsent(notification.analyticsAllowed);
      }
    } catch {
      // Ignore unrelated or malformed notifications.
    }
  });
  window.addEventListener('pageshow', initializePrivacy);
  window.addEventListener('focus', initializePrivacy);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') initializePrivacy();
  });

  initializePrivacy();
})();
